import {
  Checkbox,
  Icon,
  IconProps,
  SimpleDialog,
  Select,
  Switch,
  Tag,
  Text,
  TextProps,
} from "@ttc3k/trekker";
import {
  Eye,
  Group,
  SendDiagonal,
  ThumbsUp,
  XmarkCircleSolid,
} from "iconoir-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Collapsible,
  ControlledInput,
  InviteStaffMember,
  UploadAvatar,
} from "shared/components";
import {
  InviteStaffMemberFormValues,
  useInviteStaffMemberForm,
} from "shared/components/organisms/InviteStaffModal/useInviteStaffMemberForm";
import { WhereaboutsModal } from "shared/components/root";
import { Box, Flex, styled } from "styled-system/jsx";
import {
  TagAction,
  useInviteStaff,
  UseInviteStaffProps,
} from "./useInviteStaff";
import { useLoggedInAs, useStaffRoleAndStatus } from "shared/hooks";
import {
  AssetImage,
  ContactRole,
  EnumEntityReferenceType,
  EnumOwnerType,
  EnumUserAccessOperatorsRole,
  EnumUserAccessOperatorsStatus,
  EnumUserStatus,
  UserWithPictureTc,
} from "gql/generated";
import { useAppContext } from "useAppContext";
import { Variants } from "framer-motion";
import { defaultHiddenEmailStart } from "shared/utils";
import { ExistingUserEmailStatus } from "shared/components/molecules/InviteStaffMember/useInviteStaffMember";

type InviteStaffModalProps = {
  isOpen: boolean;
  _entityId: string;
  type: EnumEntityReferenceType;
  user?: Partial<UserWithPictureTc>;
  isForOrganization?: boolean;
  viewOnly?: boolean;
  onViewFullProfileClick: () => void;
  /** When true, modal uses max z-index (e.g. when opening from another overlay like OperatorProfileFlyout). */
  isMaxZIndex?: boolean;
  staff?: Array<{
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }>;
} & UseInviteStaffProps;

export const InviteStaffModal = ({
  _entityId,
  type,
  isOpen,
  onClose,
  setUpdatingEmail,
  refetch,
  isForOrganization = false,
  user,
  viewOnly = false,
  onViewFullProfileClick,
  isMaxZIndex = false,
  staff = [],
  onInviteComplete,
}: InviteStaffModalProps) => {
  const { t } = useTranslation("core");

  // We need to determine the default selection for the send email radio group
  // To do so, we get all of the organizations the user has access to and check
  // to see if the user is logged in as that organization. If so, we use the
  // organization's metadata to determine the default selection.
  const { dbUser, organizations } = useAppContext();
  const loggedInAs = useLoggedInAs();

  const currentOrganization =
    loggedInAs?.type === EnumOwnerType.Organization
      ? organizations.find(org => org._id === loggedInAs.entityId)
      : undefined;
  const operatorOnboardingDisabled =
    currentOrganization?.metadata?.operatorOnboardingDisabled ?? false;
  const shouldApplyOperatorOnboardingRestrictions =
    !isForOrganization && operatorOnboardingDisabled;

  let sendInvitationEmailInitial = false;
  if (
    !isForOrganization &&
    !shouldApplyOperatorOnboardingRestrictions &&
    loggedInAs &&
    loggedInAs.type === EnumOwnerType.Organization
  ) {
    if (currentOrganization) {
      sendInvitationEmailInitial =
        currentOrganization.metadata?.inviteWhenAddingStaff ?? false;
    }
  }

  // Get the user's current access to the entity
  const userAccess = useMemo(() => {
    const access = user?.access?.operators?.find(
      operator => operator?.entityId === _entityId,
    ) ??
      user?.access?.organizations?.find(
        organization => organization?.entityId === _entityId,
      ) ??
      // This modal isn't currently used for storefronts, but it could be in the future
      user?.access?.storefronts?.find(
        storefront => storefront?.entityId === _entityId,
      ) ?? {
        role: EnumUserAccessOperatorsRole.Editor,
        status: EnumUserAccessOperatorsStatus.NotInvited,
        entityId: _entityId,
      };
    return access;
  }, [user, _entityId]);

  const {
    confirmModalInfo,
    isConfirmationModalOpen,
    isUpdatingUser,
    handleCloseClick,
    handleConfirmationModalActionClick,
    handleOpenConfirmationModal,
    handleAcceptClick,
    handleInviteStaffClick,
    handleCreateOrResendInviteClick,
    handleUpdateAvatarOrRoleClick,
  } = useInviteStaff({
    _entityId,
    type,
    onClose,
    onInviteComplete,
    setUpdatingEmail,
    refetch,
    user,
  });

  // Initialize the form and zod
  const [formError, setFormError] = useState<string | undefined>();
  const { form, onSubmit, defaultValues } = useInviteStaffMemberForm({
    user,
    userAccess,
    sendInvitationEmail: sendInvitationEmailInitial,
    dataProcessor: handleInviteStaffClick,
    onSuccess: () => {
      form.reset(defaultValues);
      onClose();
    },
    onError: (message?: string) => {
      setFormError(message);
    },
  });

  // Ensure the form doesn't start off displaying an email validation error
  // when you start typing in that box (which happens AFTER sending an invitation
  // and then re-opening the modal)
  useEffect(() => {
    if (!isOpen) {
      form.reset();
    } else {
      // Reset form with default values when modal opens to ensure fields are populated correctly
      // Note: useFormHandler already handles resetting when defaultValues change via useDeepEffect,
      // so we only need to reset when the modal opens
      form.reset(defaultValues);
      if (shouldApplyOperatorOnboardingRestrictions && !user) {
        form.setValue("role", EnumUserAccessOperatorsRole.NoAccess);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen to avoid infinite loops

  const { status } = useStaffRoleAndStatus({
    user,
    entityId: _entityId,
  });

  const { roleDisplay, roleTagColor } = useStaffRoleAndStatus({
    user,
    entityId: _entityId,
  });

  const sendInvitationEmail = form.watch("sendInvitationEmail");
  const role = form.watch("role");
  const contactRoles = form.watch("contactRoles") ?? [];
  const [primaryContactReplacementUserId, setPrimaryContactReplacementUserId] =
    useState<string | undefined>();
  const [isReassignPrimaryContactModalOpen, setIsReassignPrimaryContactModalOpen] =
    useState(false);
  const isOperatorStaffModal = type === EnumEntityReferenceType.Operator;
  const isPrimaryContact =
    ((userAccess?.contactRoles ?? []).filter(Boolean) as unknown as ContactRole[])
      .includes(ContactRole.PrimaryContact) ?? false;
  const replacementStaff = useMemo(
    () => staff.filter(staffMember => staffMember._id !== user?._id),
    [staff, user?._id],
  );
  const replacementStaffCollection = useMemo(
    () =>
      Select.createCollection({
        items: replacementStaff.map(staffMember => ({
          value: staffMember._id ?? "",
          label:
            `${staffMember.firstName ?? ""} ${staffMember.lastName ?? ""}`.trim() ||
            staffMember.email ||
            "",
        })),
      }),
    [replacementStaff],
  );

  const setContactRoleChecked = (
    contactRole: ContactRole,
    checked: boolean,
  ) => {
    form.setValue(
      "contactRoles",
      checked
        ? [...new Set([...contactRoles, contactRole])]
        : contactRoles.filter(role => role !== contactRole),
      { shouldDirty: true },
    );
  };

  const getContactRoleLabel = (contactRole: ContactRole) =>
    contactRole === ContactRole.PrimaryContact
      ? t("STAFF.ADD_EDIT_PERSON.LABELS.PRIMARY_CONTACT")
      : t("STAFF.ADD_EDIT_PERSON.LABELS.FINANCIAL_CONTACT");

  const previousSendInvitationEmail = useRef(sendInvitationEmailInitial);
  useEffect(() => {
    if (role === EnumUserAccessOperatorsRole.NoAccess) {
      previousSendInvitationEmail.current = form.getValues(
        "sendInvitationEmail",
      );
      form.setValue("sendInvitationEmail", false);
    } else {
      form.setValue("sendInvitationEmail", previousSendInvitationEmail.current);
    }
  }, [form, role]);

  const [shouldDisableForm, setShouldDisableForm] = useState(false);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const actionsHidden = viewOnly && loggedInAs?.type === EnumOwnerType.Operator;

  const formId = "invite-staff-modal-form";

  return (
    <>
      <WhereaboutsModal
        isOpen={isOpen}
        isMaxZIndex={isMaxZIndex}
        onClose={() =>
          handleCloseClick(form, defaultValues as InviteStaffMemberFormValues)
        }
        hideCloseTrigger={form.formState.isSubmitting}
        initialFocus={null}
        title={
          user
            ? t("STAFF.ADD_EDIT_PERSON.TITLE_EDIT")
            : t("STAFF.ADD_EDIT_PERSON.TITLE_ADD")
        }
        HeaderIcon={Group}
        cancelButtonProps={{
          onClick: () =>
            handleCloseClick(
              form,
              defaultValues as InviteStaffMemberFormValues,
            ),
        }}
        saveButtonCopy={
          !user
            ? sendInvitationEmail
              ? t("STAFF.ADD_EDIT_PERSON.CTA_BUTTON.INVITE")
              : t("STAFF.ADD_EDIT_PERSON.CTA_BUTTON.ADD")
            : t("STAFF.ADD_EDIT_PERSON.CTA_BUTTON.SAVE")
        }
        saveButtonProps={{
          visual: "accent.blue",
          form: formId,
          type: !user ? "submit" : "button",
          onClick: async () => {
            if (user) {
              const isFormValid = await form.trigger();
              if (isFormValid) {
                const success = await handleUpdateAvatarOrRoleClick(
                  user,
                  form.getValues(),
                  userAccess.role,
                );
                if (success) {
                  form.reset(defaultValues);
                  onClose();
                }
              }
            }
          },
          isLoading: form.formState.isSubmitting || isUpdatingUser,
          disabled:
            !form.formState.isDirty ||
            form.formState.isSubmitting ||
            shouldDisableForm ||
            viewOnly ||
            isValidatingEmail,
        }}
        disableBodyPadding={true}
      >
        <Box p={{ base: "250", md: "36px" }}>
          <FormProvider {...form}>
            <form onSubmit={onSubmit} id={formId}>
              <Flex flexDir="column" alignItems="flex-start" gap="300">
                <UploadAvatar
                  image={user?.photo as Partial<AssetImage>}
                  onUploadComplete={image => {
                    if (image?._id) {
                      form.setValue("photo", image._id, { shouldDirty: true });
                    }
                  }}
                  onRemoveClick={() => {
                    form.setValue("photo", "$unset", { shouldDirty: true });
                  }}
                  isAvatarLocked={
                    user &&
                    user.photoUploadedBy === user._id &&
                    dbUser?._id !== user._id
                  }
                  AvatarTag={
                    user ? (
                      <Tag
                        visual={roleTagColor}
                        mode={roleTagColor === "primary" ? "light" : "dark"}
                      >
                        {roleDisplay}
                      </Tag>
                    ) : undefined
                  }
                  viewOnly={viewOnly}
                />

                <InviteStaffMember
                  user={user}
                  entityId={_entityId}
                  isForOrganization={isForOrganization}
                  form={form}
                  inputNameEmailProps={{ name: `email` }}
                  inputNameFirstNameProps={{ name: `firstName` }}
                  inputNameLastNameProps={{ name: `lastName` }}
                  inputNameRoleProps={{ name: `role` }}
                  onExistingUserEmailStatusChange={status =>
                    setShouldDisableForm(
                      status === ExistingUserEmailStatus.Duplicate ||
                        status === ExistingUserEmailStatus.AlreadyAttached,
                    )
                  }
                  onValidatingEmailChange={setIsValidatingEmail}
                  viewOnly={viewOnly}
                  hideRoleField={shouldApplyOperatorOnboardingRestrictions}
                />

                {isOperatorStaffModal && (
                  <ControlledInput
                    name="title"
                    label={t("STAFF.ADD_EDIT_PERSON.LABELS.TITLE")}
                    placeholder={t(
                      "STAFF.ADD_EDIT_PERSON.LABELS.TITLE_PLACEHOLDER",
                    )}
                    w="full"
                    disabled={viewOnly}
                  />
                )}

                {isOperatorStaffModal && (
                  <Flex flexDir="column" gap="100" w="full" mt="-100">
                    <Text visual="smallSemiBold">
                      {t("STAFF.ADD_EDIT_PERSON.LABELS.STAFF_ROLE")}
                    </Text>
                    {[
                      ContactRole.PrimaryContact,
                      ContactRole.FinancialContact,
                    ].map(contactRole => (
                      <Checkbox
                        key={contactRole}
                        name={`contactRole-${contactRole}`}
                        label={getContactRoleLabel(contactRole)}
                        checked={contactRoles.includes(contactRole)}
                        onCheckedChange={({ checked }) =>
                          setContactRoleChecked(contactRole, checked === true)
                        }
                        disabled={viewOnly}
                      />
                    ))}
                  </Flex>
                )}

                {/* Determine if we need to send an email when the staff member(s) is added
                - This is only applicable for non-ORG staff
                - This is also only applicable for new users (i.e. no _id)
              */}
                {!user && !shouldApplyOperatorOnboardingRestrictions && (
                  <Collapsible
                    open={role !== EnumUserAccessOperatorsRole.NoAccess}
                    additionalVariantProps={
                      {
                        open: { display: "block" },
                        closed: { display: "none" },
                      } as Variants
                    }
                  >
                    <Flex
                      flexDir="column"
                      gap="150"
                      borderTop="1px solid"
                      borderColor="border.light"
                      pt="300"
                    >
                      <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        gap="100"
                        position="relative"
                        w="full"
                      >
                        <Flex alignItems="center" gap="150">
                          <Switch
                            checked={sendInvitationEmail}
                            onCheckedChange={({ checked }) =>
                              form.setValue(
                                "sendInvitationEmail",
                                checked === true,
                                { shouldDirty: true },
                              )
                            }
                          />
                          <Text visual="bodySemiBold">
                            {t(
                              "STAFF.ADD_EDIT_PERSON.LABELS.INVITE_TO_JOIN_LISTING",
                            )}
                          </Text>
                        </Flex>
                        <Tag visual="success" mode="light">
                          {t("STAFF.ADD_EDIT_PERSON.LABELS.OPTIONAL")}
                        </Tag>
                      </Flex>

                      <Text color="text.mid">
                        {t(
                          "STAFF.ADD_EDIT_PERSON.LABELS.INVITE_TO_JOIN_LISTING_DESCRIPTION",
                        )}
                      </Text>
                    </Flex>
                  </Collapsible>
                )}

                {!!user && !actionsHidden && (
                  <Flex flexDir="column" gap="150" w="full">
                    <Text visual="smallSemiBold">
                      {t("STAFF.ADD_EDIT_PERSON.LABELS.ACTIONS")}
                    </Text>
                    <Flex flexDir="column" gap="100" w="full">
                      {/*
                      ONLY organization users can view the full profile. ALL users can view this info (even viewOnly)
                      */}
                      {loggedInAs?.type === EnumOwnerType.Organization && (
                        // View full profile
                        <ActionItem
                          iconProps={{
                            Element: Eye,
                            color: "icon.mid",
                          }}
                          textProps={{
                            color: "text.mid",
                            children: t(
                              "STAFF.ADD_EDIT_PERSON.CTA_BUTTON.VIEW_DETAILS",
                            ),
                          }}
                          onClick={() => {
                            onViewFullProfileClick();
                            onClose();
                          }}
                        />
                      )}

                      {/*
                      If a user has not been invited, we can send an invite.
                      Also covers operator staff who have Accepted access but
                      have not yet been provisioned (no Auth0 account / invite).
                      */}
                      {!viewOnly &&
                        !shouldApplyOperatorOnboardingRestrictions &&
                        (status === EnumUserAccessOperatorsStatus.NotInvited ||
                          (status === EnumUserAccessOperatorsStatus.Accepted &&
                            user?.status !== EnumUserStatus.Enrolled &&
                            user?.status !== EnumUserStatus.Provisioned)) &&
                        !user?.email?.startsWith(defaultHiddenEmailStart) && (
                          // Send invite
                          <ActionItem
                            iconProps={{
                              Element: SendDiagonal,
                              color: "icon.mid",
                            }}
                            textProps={{
                              color: "text.mid",
                              children: t(
                                "STAFF.ADD_EDIT_PERSON.CTA_BUTTON.SEND_INVITE",
                              ),
                            }}
                            onClick={async () => {
                              await handleCreateOrResendInviteClick(
                                {
                                  email:
                                    form.getValues("email") ??
                                    user?.email ??
                                    "",
                                  firstName: form.getValues("firstName"),
                                  lastName: form.getValues("lastName"),
                                  title: form.getValues("title"),
                                  photo: form.getValues("photo"),
                                  role: form.getValues("role"),
                                  contactRoles: form.getValues("contactRoles"),
                                },
                                true, // Inviting for the first time
                                true, // Yes, we want to send an email
                              );
                              onClose();
                            }}
                          />
                        )}

                      {/*
                      If a user has been invited, we can resend the invite.
                      Also covers operator staff who have Accepted access but
                      are only Provisioned (invited but haven't logged in yet).
                      */}
                      {!viewOnly &&
                        !shouldApplyOperatorOnboardingRestrictions &&
                        (status === EnumUserAccessOperatorsStatus.Invited ||
                          (status === EnumUserAccessOperatorsStatus.Accepted &&
                            user?.status === EnumUserStatus.Provisioned)) &&
                        !user?.email?.startsWith(defaultHiddenEmailStart) && (
                          // Resend invite
                          <ActionItem
                            iconProps={{
                              Element: SendDiagonal,
                              color: "icon.mid",
                            }}
                            textProps={{
                              color: "text.mid",
                              children: t(
                                "STAFF.ADD_EDIT_PERSON.CTA_BUTTON.RESEND_INVITE",
                              ),
                            }}
                            onClick={async () => {
                              await handleCreateOrResendInviteClick(
                                {
                                  email:
                                    form.getValues("email") ??
                                    user?.email ??
                                    "",
                                  firstName: form.getValues("firstName"),
                                  lastName: form.getValues("lastName"),
                                  title: form.getValues("title"),
                                  photo: form.getValues("photo"),
                                  role: form.getValues("role"),
                                  contactRoles: form.getValues("contactRoles"),
                                },
                                false, // Not inviting for the first time
                                true, // Yes, we want to send an email
                              );
                              onClose();
                            }}
                          />
                        )}

                      {/*
                      When a user has requested access to an entity, aside from viewing the details,
                      the only other actions are to approve or delete the request
                      */}
                      {!viewOnly &&
                        status === EnumUserAccessOperatorsStatus.Requested && (
                          <>
                            {/* Accept request */}
                            <ActionItem
                              iconProps={{
                                Element: ThumbsUp,
                                color: "icon.mid",
                              }}
                              textProps={{
                                color: "text.mid",
                                children: t(
                                  "STAFF.ADD_EDIT_PERSON.CTA_BUTTON.ACCEPT_REQUEST",
                                ),
                              }}
                              onClick={() => {
                                handleAcceptClick(
                                  user?._id ?? "",
                                  user?.email ?? "",
                                );
                                onClose();
                              }}
                            />
                            {/* Delete request */}
                            <ActionItem
                              iconProps={{
                                Element: XmarkCircleSolid,
                                color: "icon.error.mid",
                              }}
                              textProps={{
                                color: "text.error.mid",
                                children: t(
                                  "STAFF.ADD_EDIT_PERSON.CTA_BUTTON.REMOVE",
                                ),
                              }}
                              onClick={() =>
                                handleOpenConfirmationModal(
                                  TagAction.Delete,
                                  user?._id ?? "",
                                  user?.email ?? "",
                                )
                              }
                            />
                          </>
                        )}

                      {/*
                      If a using is any status OTHER than REQUESTED, they can be removed from the listing
                      */}
                      {!viewOnly &&
                        status !== EnumUserAccessOperatorsStatus.Requested && (
                          // Remove from listing
                          <ActionItem
                            iconProps={{
                              Element: XmarkCircleSolid,
                              color: "icon.error.mid",
                            }}
                            textProps={{
                              color: "text.error.mid",
                              children: t(
                                "STAFF.ADD_EDIT_PERSON.CTA_BUTTON.REMOVE",
                              ),
                            }}
                            onClick={() =>
                              isOperatorStaffModal &&
                              isPrimaryContact &&
                              replacementStaff.length > 0
                                ? setIsReassignPrimaryContactModalOpen(true)
                                : handleOpenConfirmationModal(
                                    TagAction.Remove,
                                    user?._id ?? "",
                                    user?.email ?? "",
                                  )
                            }
                          />
                        )}
                    </Flex>
                  </Flex>
                )}
              </Flex>
            </form>
          </FormProvider>
          {formError && (
            <Text visual={"smallSemiBold"} color={"text.error.dark"} mt={"100"}>
              {formError}
            </Text>
          )}
        </Box>
      </WhereaboutsModal>
      <SimpleDialog
        open={isConfirmationModalOpen}
        title={t("STAFF.CONFIRM_MODAL.TITLE")}
        message={t(`STAFF.CONFIRM_MODAL.${confirmModalInfo?.action}`)}
        cancelButtonCopy={t("CONFIRM.NO")}
        onCancelClick={() => handleConfirmationModalActionClick(false)}
        confirmButtonCopy={t("CONFIRM.YES")}
        confirmButtonVisual="destructive"
        onConfirmClick={() => handleConfirmationModalActionClick(true)}
        size="md"
      />
      <WhereaboutsModal
        isOpen={isReassignPrimaryContactModalOpen}
        isMaxZIndex={true}
        onClose={() => setIsReassignPrimaryContactModalOpen(false)}
        title={t("STAFF.REASSIGN_PRIMARY_CONTACT_MODAL.TITLE")}
        cancelButtonProps={{
          onClick: () => setIsReassignPrimaryContactModalOpen(false),
        }}
        saveButtonCopy={t("STAFF.REASSIGN_PRIMARY_CONTACT_MODAL.CONFIRM")}
        saveButtonProps={{
          visual: "accent.blue",
          disabled: !primaryContactReplacementUserId,
          onClick: () => {
            handleOpenConfirmationModal(
              TagAction.Remove,
              user?._id ?? "",
              user?.email ?? "",
              primaryContactReplacementUserId,
            );
            setIsReassignPrimaryContactModalOpen(false);
          },
        }}
      >
        <Flex flexDir="column" gap="200" p="300">
          <Text>{t("STAFF.REASSIGN_PRIMARY_CONTACT_MODAL.DESCRIPTION")}</Text>
          <Select.Root
            collection={replacementStaffCollection}
            value={
              primaryContactReplacementUserId
                ? [primaryContactReplacementUserId]
                : []
            }
            onValueChange={({ value }) =>
              setPrimaryContactReplacementUserId(value[0])
            }
            label={t("STAFF.REASSIGN_PRIMARY_CONTACT_MODAL.LABEL")}
          >
            {replacementStaffCollection.items.map(item => (
              <Select.Item key={item.value} item={item} />
            ))}
          </Select.Root>
        </Flex>
      </WhereaboutsModal>
    </>
  );
};

type ActionItemProps = {
  iconProps?: IconProps;
  textProps?: TextProps;
  onClick: () => void;
};

const ActionItem = ({ iconProps, textProps, onClick }: ActionItemProps) => {
  return (
    <styled.button
      display="flex"
      alignItems="center"
      gap="75"
      p="200"
      borderRadius="100"
      bgColor="bg.wa.lightest"
      _hover={{
        bgColor: "bg.wa.mid",
      }}
      _focusVisible={{
        bgColor: "bg.wa.mid",
      }}
      cursor="pointer"
      w="full"
      onClick={onClick}
      type="button"
    >
      <Icon {...iconProps} />
      <Text visual="smallSemiBold" {...textProps} />
    </styled.button>
  );
};
